<template>
    <div class="flex flex-col h-full">
        <page-header :buttons="pageActions.buttons" :additional="pageActions.additional" @method="methodHandler">
            <template slot="title">{{ item.name }}<small class="text-xs ml-2 hidden xl:inline-block">Suppliers</small></template>
        </page-header>

        <tabs :tabs="tabs" :active="startTab" @setTab="setActiveTab" ref="tabs"></tabs>

        <div class="flex xl:flex-row lg:flex-col md:flex-col sm:flex-col flex-grow">
            <div class="absolute top-0 left-0 right-0 bottom-0 w-full overflow-hidden flex items-center text-center justify-center z-50" v-show="loading"><spinner line-fg-color="#922C49" size="55" :line-size="5"></spinner></div>
            <div class="lg:w-full md:w-full sm:w-full px-4 sm:px-6 lg:px-6 bg-white border-r border-gray-400 bg-gray-200" :class="[ activeTab === 'communications' ? 'xl:w-full' : 'xl:w-3/4' ]" v-if="!loading">
                <div class="mt-4 grid grid-cols-1 row-gap-2 col-gap-4 sm:grid-cols-12" v-if="!loading" v-show="activeTab === 'details'">
                    <div class="sm:col-span-6">
                        <table class="w-full internal-table">
                            <thead>
                                <tr>
                                    <th colspan="2">Supplier Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="w-32 font-black">Name</td>
                                    <td>{{ item.name }}</td>
                                </tr>
                                <tr>
                                    <td>Types</td>
                                    <td>{{ commaSeparatedTypes }}</td>
                                </tr>
                                <tr>
                                    <td>Account Type</td>
                                    <td v-if="item.accountType">{{ item.accountType.name }}</td>
                                    <td v-else>N/A</td>
                                </tr>
                                <tr>
                                    <td>Street Address</td>
                                    <td>{{ item.address }} {{ item.suburb }} {{ item.state }}  {{ item.postcode }}</td>
                                </tr>
                                <tr>
                                    <td>Postal Address</td>
                                    <td>{{ item.postal_address }} {{ item.postal_suburb }} {{ item.postal_state }}  {{ item.postal_postcode }}</td>
                                </tr>
                                <tr>
                                    <td>ABN</td>
                                    <td>{{ item.abn }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="sm:col-span-6">
                        <table class="w-full internal-table">
                            <thead>
                                <tr>
                                    <th colspan="2">Contact Information</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Contact</td>
                                    <td>{{ item.contact_first }} {{ item.contact_last }}</td>
                                </tr>
                                <tr>
                                    <td>Phone</td>
                                    <td>{{ item.phone }}</td>
                                </tr>
                                <tr>
                                    <td>Mobile</td>
                                    <td>{{ item.mobile }}</td>
                                </tr>
                                <tr>
                                    <td>Primary Email</td>
                                    <td>{{ item.email }}</td>
                                </tr>
                                <tr v-if="item.additional_emails">
                                    <td>Additional Emails</td>
                                    <td>{{ item.additional_emails }}</td>
                                </tr>
                                <tr>
                                    <td>Sales URL</td>
                                    <td>{{ item.website }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="sm:col-span-6">
                        <table class="w-full internal-table">
                            <thead>
                                <tr>
                                    <th colspan="2">Bank Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td class="w-32 font-black">Account Name</td>
                                    <td>{{ item.account_name }}</td>
                                </tr>
                                <tr>
                                    <td>BSB</td>
                                    <td>{{ item.account_bsb }}</td>
                                </tr>
                                <tr>
                                    <td>Account #</td>
                                    <td>{{ item.account_number }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="sm:col-span-6">
                        <table class="w-full internal-table">
                            <thead>
                            <tr>
                                <th colspan="2">BPay Details</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr>
                                <td class="w-32 font-black">Biller Code</td>
                                <td>{{ item.bpay_biller_code }}</td>
                            </tr>
                            <tr>
                                <td>CRN</td>
                                <td>{{ item.bpay_crn }}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="sm:col-span-12" v-if="item.documents && item.documents.length > 0">
                        <table class="w-full mb-4 internal-table">
                            <thead>
                            <tr>
                                <th>Documents/Files</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(file, index) in item.documents" :key="index">
                                <td style="font-weight: normal;"><a :href="s3URL + '/' + file.path " target="_blank" class="text-gray-800 hover:text-default-500 truncate">{{ file.filename }}</a></td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <tasks v-if="!loading"
                    v-show="activeTab === 'tasks'"
                    :id="item.id"
                    :tasks="item.tasks"
                    model="Supplier" />
                <notes v-if="!loading"
                    v-show="activeTab === 'notes'"
                    :id="item.id"
                    :notes="item.notes"
                    model="Supplier" />
            </div>
        </div>
        <delete-confirmation-modal v-if="showDeleteModal" @close="closeDeleteModal" @delete="deleteItem" ref="itemDeleteModal">
            <template slot="content">Are you sure you want to delete <strong>#{{ item.name }}</strong>?</template>
        </delete-confirmation-modal>
    </div>
</template>

<script>
    import * as router from "vue-router";
    import axios from "axios";
    import Tabs from "@/mixins/Tabs";
    import Notes from "@/components/Notes";
    import Tasks from '@/components/Tasks';
    import DeleteModal from "@/mixins/DeleteModal";

    export default {
        name: "SupplierView",
        components: {
            Notes,
            Tasks
        },
        props: {
            id: { required: true },
            return: { required: false, default: '/configuration/suppliers' },
            page: { required: false, default: 'suppliers' }
        },
        mixins: [
            Tabs,
            DeleteModal
        ],
        data: function () {
            return {
                resource: 'suppliers',
                member: {},
                startTab: null,
                loading: true,
                item: {},
                pageActions: {},
                showEmailModal: false,
                s3URL: process.env.VUE_APP_S3_URL,
            }
        },
        methods: {
            emailSent: function(){
                this.showEmailModal = false;
                this.getData();
            },
            smsSent: function(){
                this.showSmsModal = false;
                this.getData();
            },
            getData: function(){
                this.$Progress.start();

                axios.get(process.env.VUE_APP_API_URL + '/suppliers/'+this.id)
                .then(response => {
                    this.item = response.data;

                    this.pageActions = {
                        buttons: [
                            {
                                label: 'Edit',
                                icon: 'edit',
                                type: 'router-link',
                                link: '/configuration/suppliers/'+ this.id + '/edit',
                                iconClass: 'text-gray-700',
                                display: !!(this.$store.getters.permissions.indexOf('suppliers.edit') !== -1 || this.permissions.indexOf('suppliers.edit') !== -1 || this.$store.getters.nimdarepus)
                            },
                            {
                                label: 'Close',
                                icon: 'times',
                                type: 'router-link',
                                iconClass: 'text-red-500',
                                link: this.return ? this.return : '/configuration/suppliers',
                            },
                        ],
                        additional: [
                            {
                                label: 'Delete',
                                icon: 'trash-alt',
                                type: 'method',
                                method: 'deleteModal',
                                iconClass: 'text-red-500',
                                display: !!(this.$store.getters.permissions.indexOf('suppliers.destroy') !== -1 || this.$store.getters.nimdarepus)
                            }
                        ]
                    };

                    this.$Progress.finish();
                    this.loading = false;
                })
                .catch(() => {
                    this.$Progress.finish();
                    this.loading = false;
                });
            },
            methodHandler: function (method) {
                this[method]();
            },
            actionHandler: function (data) {
                this[data.method](data);
            },
            openEmailEditor: function(data) {
                this.showEmailModal = true;
            }
        },
        beforeMount() {

        },
        mounted() {
            this.getData();
        },
        computed: {
            tabs() {
                return [
                    {
                        name: 'Details',
                        slug: 'details'
                    },
                    {
                        name: 'Tasks',
                        slug: 'tasks',
                    },
                    {
                        name: 'Notes',
                        slug: 'notes',
                    }
                ];
            },
            commaSeparatedTypes () {
                const fieldNames = this.item.types.map(object => {
                    return object.name;
                });
                return fieldNames.join(', ');
            },
        }
    }
</script>

<style scoped>

</style>
